<template>
	<aside
		v-if="filterGroups && filterGroups.length > 0"
		class="col-12_sm-12_md-3 list-filters"
		ref="listFiltersRef"
	>
		<div class="form filters">
			<details
				:open="openState"
				v-if="isViewportSmall"
				class="list-filters__accordion"
			>
				<summary id="filters-headline"></summary>
				<div id="filters-content"></div>
			</details>
			<Teleport :to="headLineTarget" :disabled="!headLineTarget">
				<component
					:is="headerType"
					class="section__title list-filters__filter-headline"
					v-if="filterBy"
				>
					{{ filterBy }}
				</component>
			</Teleport>
			<Teleport :to="contentTarget" :disabled="!contentTarget">
				<div
					v-if="showDateFilters || showTextFilter"
					class="list-filters__filter-group"
				>
					<div v-if="showDateFilters" class="grid">
						<div
							class="col-12 col__datepicker list-filters__filter-col"
						>
							<p
								:class="{
									hidden: fromDateValid,
								}"
								class="datepicker__error"
								:ref="'startDateError'"
								tabindex="-1"
								:id="'errorstartDate'"
							>
								{{ invalidMessage }}
							</p>
							<Datepicker
								id="startDate"
								name="startDate"
								label="Fra"
								:value="fromDate"
								:invalid="!fromDateValid"
								@update="updateFromDate"
								@invalid="fromDateInValid"
								:defaultDate="defaultFromDate"
								:disableInvalidUpdate="true"
								:ariaLabel="ariaLabelChooseFromDate"
								:describedBy="'errorstartDate'"
							>
							</Datepicker>
						</div>

						<div
							class="col-12 col__datepicker list-filters__filter-col"
						>
							<p
								:class="{
									hidden: endDateValid,
								}"
								class="datepicker__error"
								tabindex="-1"
								:ref="'endDateError'"
								:id="'errorendDate'"
							>
								{{ invalidMessage }}
							</p>
							<Datepicker
								id="endDate"
								name="endDate"
								label="Til"
								:value="toDate"
								@update="updateToDate"
								:defaultDate="defaultToDate"
								:ariaLabel="ariaLabelChooseToDate"
								:invalid="!endDateValid"
								@invalid="toDateInValid"
								:disableInvalidUpdate="true"
								:describedBy="'errorendDate'"
							>
							</Datepicker>
						</div>
					</div>

					<div v-if="showTextFilter" class="list-filters__filter-col">
						<div role="search" class="form-search">
							<label for="list-filters-search-form-input">{{
								searchLabel
							}}</label>
							<button
								class="list-filters__search-button"
								type="submit"
								:title="searchLabel"
								:aria-label="searchLabel"
							>
								<div class="icon icon-magnifier">
									<svg
										data-name="Layer 1"
										version="1.1"
										width="20px"
										height="24px"
										viewBox="0 0 23.73 23.73"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="m23.73 22.31-5.63-5.62a10.21 10.21 0 1 0-1.41 1.41l5.62 5.63zm-13.51-3.89a8.2 8.2 0 1 1 8.2-8.2 8.21 8.21 0 0 1-8.2 8.2z"
										></path>
									</svg>
								</div>
							</button>
							<input
								class="list-filters__filters-search-input"
								name="searchrequest"
								id="list-filters-search-form-input"
								type="search"
								:placeholder="searchPlaceholder"
								:title="searchLabel"
								autocomplete="off"
								aria-autocomplete="list"
								aria-haspopup="true"
								:aria-label="searchLabel"
								v-model="searchTerm"
								@change="updateSearchTerm"
							/>
						</div>
					</div>
				</div>
				<div v-if="isViewportSmall">
					<div class="grid-1">
						<div
							class="col"
							v-for="filter in filtersArray"
							:key="filter.name"
						>
							<dropdown>
								<template #btn>
									<div class="bp-dropdown__btn-text">
										{{ filter.displayName }}
										<span
											class="filters__filter-badge"
											v-if="hasActiveFilters(filter)"
										>
											{{ hasActiveFilters(filter) }}
										</span>
									</div>
								</template>
								<template #body>
									<ul>
										<li
											v-for="filteritem in filter.values"
											:key="filteritem.displayName"
										>
											<label class="checkbox">
												<input
													type="checkbox"
													v-model="filteritem.value"
													@change="updateTagFilter()"
												/>
												<span
													class="checkbox__checkmark"
												></span>
												{{
													translate(
														filteritem.displayName
													)
												}}
											</label>
											<span class="filters__filter-amount"
												>({{ filteritem.count }})</span
											>
										</li>
									</ul>
								</template>
							</dropdown>
						</div>
					</div>
				</div>

				<div v-else class="list-filters__filter-group">
					<fieldset v-for="group in filtersArray">
						<legend>{{ group.name }}</legend>
						<ul
							v-if="
								group && group.values && group.values.length > 0
							"
							class="ul--nostyling form"
						>
							<li
								v-for="filterItem in group.values"
								class="filter"
							>
								<label class="checkbox"
									><input
										type="checkbox"
										v-model="filterItem.value"
										@change="updateTagFilter()"
									/>
									<span class="checkbox__checkmark"></span>
									{{ translate(filterItem.displayName) }}
								</label>
								<span class="filters__filter-amount"
									>({{ filterItem.count }})</span
								>
							</li>
						</ul>
					</fieldset>
				</div>
			</Teleport>
		</div>
	</aside>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import Datepicker from "./../../datepicker/datepicker.vue";
import Dropdown from "./../../../shared/script/vue/Dropdown.vue";
import { isDateValid } from "../../../shared/script/utilities";

const props = defineProps({
	headerType: {
		type: String,
		default: "h2",
	},
	value: Object,
	chosenFilters: {
		type: Object,
		default: () => ({}),
	},
	filters: Object,
	searchPlaceholder: {
		type: String,
		default: "",
	},
	searchLabel: {
		type: String,
		default: "",
	},
	ariaLabelFilterOnYears: {
		type: String,
		default: "",
	},
	filterBy: {
		type: String,
		default: "",
	},
	availableSubjects: {
		type: Array,
		default: () => [],
	},
	showDateFilters: {
		type: Boolean,
		default: false,
	},
	showTextFilter: {
		type: Boolean,
		default: false,
	},
	showYearFilter: {
		type: Boolean,
		default: false,
	},
	defaultTagIds: {
		type: Array,
		default: () => [],
	},
	buttonText: {
		type: String,
		default: "Søg",
	},
	invalidMessage: {
		type: String,
		default: "Angiv korrekt datoformat, fx 01.02.2024.",
	},
	searchTermDefault: String,
	toDateDefault: String,
	fromDateDefault: String,
	ariaLabelChooseFromDate: {
		type: String,
		default: "Vælg fra dato",
	},
	ariaLabelChooseToDate: {
		type: String,
		default: "Vælg til dato",
	},
	ariaLabelChooseSubject: {
		type: String,
		default: "Vælg emne",
	},
	filterGroups: {
		type: Array,
		default: () => [],
	},
});

const emit = defineEmits([
	"update",
	"updateFromDate",
	"updateToDate",
	"updateSearchTerm",
	"updateTagFilter",
	"submit",
]);

const screenSize = ref("");
const activeFilters = ref({});
const openState = ref(true);
const fromDate = ref("");
const toDate = ref("");
const searchTerm = ref("");
const selectedTags = ref([]);
const filtersArray = ref([]);
const fromDateValid = ref(true);
const endDateValid = ref(true);
const defaultFromDate = ref("");
const defaultToDate = ref("");
const headLineTarget = ref(null);
const contentTarget = ref(null);

const listFiltersRef = ref("listFiltersRef");

onMounted(() => {
	activeFilters.value = props.chosenFilters;

	if (props.fromDateDefault) {
		const fromDateObj = new Date(props.fromDateDefault);
		fromDate.value = `${fromDateObj
			.getDate()
			.toString()
			.padStart(2, "0")}.${(fromDateObj.getMonth() + 1)
			.toString()
			.padStart(2, "0")}.${fromDateObj.getFullYear()}`;
	}
	if (props.toDateDefault) {
		const toDateObj = new Date(props.toDateDefault);
		toDate.value = `${toDateObj.getDate().toString().padStart(2, "0")}.${(
			toDateObj.getMonth() + 1
		)
			.toString()
			.padStart(2, "0")}.${toDateObj.getFullYear()}`;
	}

	if (props.defaultTagIds) {
		selectedTags.value = props.defaultTagIds;
	}

	searchTerm.value = props.searchTermDefault;

	if (props.filterGroups) {
		const arr = props.filterGroups.map((group) => {
			const valueArray = group.values.map((item) => ({
				...item,
				value: selectedTags.value.includes(item.contentId),
			}));
			return { ...group, values: valueArray };
		});
		filtersArray.value = arr;
	}

	const focusableItems = listFiltersRef.value.querySelectorAll("input");

	const globalFocusableItems = document.querySelectorAll("a, button, input");
	globalFocusableItems.forEach((element) => {
		element.addEventListener("keyup", (e) => {
			if (e.key === "Tab") {
				focusableItems.forEach((item) => {
					if (item.id === document.activeElement.id) {
						item.classList.add("tab-focused");
					}
				});
			}
		});
	});
	focusableItems.forEach((element) => {
		element.addEventListener("blur", () => {
			element.classList.remove("tab-focused");
		});
	});
});

const onCheckboxChange = () => {
	emit("update", activeFilters.value);
};

const updateFromDate = (e) => {
	fromDateValid.value = isDateValid(e);
	if (fromDateValid.value) {
		fromDate.value = e;
		emit("updateFromDate", e);
	} else {
		nextTick(() => {
			document.querySelector("#startDateError").focus();
		});
	}
};

const fromDateInValid = (e) => {
	if (e === "") {
		fromDate.value = e;
		fromDateValid.value = true;
		emit("updateFromDate", e);
	} else {
		fromDateValid.value = false;
		nextTick(() => {
			document.querySelector("#startDateError").focus();
		});
		fromDate.value = e;
	}
};

const toDateInValid = (e) => {
	if (e === "") {
		toDate.value = e;
		endDateValid.value = true;
		emit("updateToDate", e);
	} else {
		endDateValid.value = false;
		toDate.value = e;
		nextTick(() => {
			document.querySelector("#endDateError").focus();
		});
	}
};

const updateToDate = (e) => {
	endDateValid.value = isDateValid(e);
	if (endDateValid.value) {
		toDate.value = e;
		emit("updateToDate", e);
	} else {
		nextTick(() => {
			document.querySelector("#endDateError").focus();
		});
	}
};

const updateSearchTerm = () => {
	emit("updateSearchTerm", searchTerm.value);
};

const updateAriaSelected = (e) => {
	const optionEL = e.target.parentElement.parentElement;
	optionEL.setAttribute("aria-selected", e.target.checked);
};

const updateTagFilter = () => {
	let activeFilters = [];
	if (filtersArray.value && filtersArray.value.length > 0) {
		filtersArray.value.forEach((filters) => {
			activeFilters = activeFilters.concat(
				filters.values.filter((filterItem) => filterItem.value === true)
			);
		});
	}
	emit("updateTagFilter", activeFilters);
};

const update = (e) => {
	if (e.target && e.target.nodeName === "INPUT") {
		updateAriaSelected(e);
	}
	emit("update", props.value);
};

const submit = () => {
	emit("updateSearchTerm", searchTerm.value);
	emit("submit");
};

const hasActiveFilters = (filter) => {
	return filter.values.some((filterItem) => filterItem.value === true);
};

const translate = (value) => {
	if (value === "True") {
		return "Ja";
	} else if (value === "False") {
		return "Nej";
	} else {
		return value;
	}
};

const ariaLabelSearch = computed(() => {
	return document.documentElement.lang === "da" ? "Søg" : "Search";
});

const isViewportSmall = computed(() => {
	return ["xsmall", "small", "smallx"].includes(screenSize.value);
});

onMounted(() => {
	screenSize.value = utilities.getSize();
	utilities.emitter.on("responsive/sizechange", (size) => {
		screenSize.value = size;
	});
});

onBeforeUnmount(() => {
	utilities.emitter.off("responsive/sizechange");
});

watch(screenSize, (newVal) => {
	if (["xsmall", "small", "smallx"].includes(newVal)) {
		headLineTarget.value = "#filters-headline";
		contentTarget.value = "#filters-content";
	} else {
		headLineTarget.value = null;
		contentTarget.value = null;
	}
});
</script>
